import { isAccountingJournalTarget } from 'common/helpers'
import { AssetLedger, LedgerSetting } from 'common/models'
import { useFormikContext } from 'formik'
import { isEqual } from 'lodash'
import get from 'lodash/get'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { selectLedgerSettingList } from 'store/settings/ledgerSetting/selectors'

import { Suggestion } from '../types'
import type { File as AISuggestionFile } from './use-ai-suggestion-state'
import { useAISuggestionState } from './use-ai-suggestion-state'
import { useAISuggestions } from './use-ai-suggestions'
import { useGetAssetCategory } from './use-get-asset-category'

export type { AIState, File } from './use-ai-suggestion-state'

export const useAISuggestionHandlers = (assetName?: string, image?: AISuggestionFile) => {
  const { assetCategoryList } = useGetAssetCategory()
  const { setFieldValue, values } = useFormikContext()
  const fixedAssetLedgers: AssetLedger[] = get(values, 'fixed_asset_ledgers')
  const ledgerSettings: LedgerSetting[] = useSelector(selectLedgerSettingList, isEqual)

  const { aiState, setAIState, responseData, hasSubmitted, onSubmit, onTryAgain, isLoading } = useAISuggestionState(
    assetName,
    image
  )

  const suggestions = useAISuggestions(responseData, aiState?.assetName)

  const findAssetCategoryByCode = useCallback(
    (assetCategoryCode: string) => assetCategoryList.find(item => item.code === assetCategoryCode),
    [assetCategoryList]
  )

  const isRegisteredCategory = useCallback(
    (suggestion?: Suggestion) => Boolean(findAssetCategoryByCode(suggestion?.asset_category_code ?? '')),
    [findAssetCategoryByCode]
  )

  const onReflect = useCallback(
    (suggestion?: Suggestion) => {
      if (!suggestion) return

      const photos = aiState?.image ? [aiState?.image] : []
      const assetCategoryInfo = findAssetCategoryByCode(suggestion?.asset_category_code as string)
      setFieldValue('asset_category_id', { value: assetCategoryInfo?.id, label: assetCategoryInfo?.name })
      setFieldValue('name', aiState?.assetName)
      setFieldValue('photos', photos)

      const accountantLedger = ledgerSettings?.find(ledger => isAccountingJournalTarget(ledger))
      const accountantLedgerIndex =
        accountantLedger?.id != null && Array.isArray(fixedAssetLedgers)
          ? fixedAssetLedgers.findIndex(ledger => ledger.ledger_setting_id === accountantLedger.id)
          : -1

      if (accountantLedgerIndex >= 0) {
        setFieldValue(`fixed_asset_ledgers[${accountantLedgerIndex}].service_life`, assetCategoryInfo?.service_life)
      }
    },
    [aiState, findAssetCategoryByCode, fixedAssetLedgers, ledgerSettings, setFieldValue]
  )

  const hasSuggestionResult = Boolean(
    responseData?.asset_category_code ||
      responseData?.asset_category_name ||
      (responseData?.alternative_matches?.length as number) > 0 ||
      responseData?.proposed_new_category
  )
  const showRunAIAnalysisButton = Boolean(aiState?.assetName || aiState?.image)
  const isInitialState = Object.keys(aiState).length === 0

  return {
    aiState,
    setAIState,
    onSubmit,
    suggestions,
    hasSuggestionResult,
    hasSubmitted,
    isLoading,
    onTryAgain,
    onReflect,
    showRunAIAnalysisButton,
    isInitialState,
    isRegisteredCategory,
  }
}
